import type {
  ClassScheduleDto,
  ClassSessionsResponse,
  ClassSessionStatus,
  SportDto,
} from "../types";

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;

const STATUS_LABELS: Record<ClassSessionStatus, string> = {
  1: "Confirmada",
  2: "Cancelada",
  3: "Concluída",
  4: "Bloqueada",
};

export interface ScheduleEntry {
  classId: string;
  sessionId: string | null;
  date: string;
  name: string;
  sportId: string | null;
  sportName: string | null;
  coachName: string;
  startTime: string;
  endTime: string;
  capacity: number;
  enrolledCount: number;
  availableSpots: number;
  status: ClassSessionStatus;
  reason: string | null;
  courtId: string;
  courtName: string;
}

interface BuildScheduleOptions {
  courtName: string;
  timeZoneId: string;
  sports: readonly SportDto[];
}

function parseDateOnly(value: string): Date {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
}

function formatDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Data local do CT (não do dispositivo) no formato `YYYY-MM-DD`. */
export function toDateOnly(date: Date, timeZoneId: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZoneId,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function addDays(dateOnly: string, days: number): string {
  const date = parseDateOnly(dateOnly);
  date.setUTCDate(date.getUTCDate() + days);
  return formatDateOnly(date);
}

/** Segunda-feira da semana que contém a data informada. */
export function mondayOf(dateOnly: string): string {
  const date = parseDateOnly(dateOnly);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return formatDateOnly(date);
}

export function weekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

export function formatWeekdayShort(dateOnly: string): string {
  return WEEKDAY_LABELS[parseDateOnly(dateOnly).getUTCDay()] ?? "";
}

export function formatWeekdayInitial(dateOnly: string): string {
  return formatWeekdayShort(dateOnly).charAt(0);
}

export function formatDayNumber(dateOnly: string): string {
  return String(parseDateOnly(dateOnly).getUTCDate()).padStart(2, "0");
}

export function formatMonthShort(dateOnly: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", { month: "short", timeZone: "UTC" })
      .format(parseDateOnly(dateOnly))
      .replace(".", "")
      .toUpperCase();
  } catch {
    return "";
  }
}

export function formatWeekRange(weekStart: string): string {
  const start = parseDateOnly(weekStart);
  const end = parseDateOnly(addDays(weekStart, 6));
  const day = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", timeZone: "UTC" });
  const month = new Intl.DateTimeFormat("pt-BR", { month: "short", timeZone: "UTC" });
  const startDay = day.format(start);
  const endDay = day.format(end);
  const startMonth = month.format(start).replace(".", "");
  const endMonth = month.format(end).replace(".", "");
  return startMonth === endMonth
    ? `${startDay} – ${endDay} ${startMonth}`
    : `${startDay} ${startMonth} – ${endDay} ${endMonth}`;
}

export function formatLongDate(dateOnly: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      timeZone: "UTC",
    }).format(parseDateOnly(dateOnly));
  } catch {
    return dateOnly;
  }
}

/**
 * Horários chegam como `HH:mm:ss` (TimeOnly do schedule) ou ISO com offset
 * (ClassSessionDto). O fuso do CT é sempre aplicado ao ISO.
 */
export function formatTimeInZone(value: string, timeZoneId: string): string {
  if (/^\d{2}:\d{2}/.test(value)) return value.slice(0, 5);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timeZoneId,
    }).format(date);
  } catch {
    return date.toISOString().slice(11, 16);
  }
}

export function statusLabel(status: ClassSessionStatus): string {
  return STATUS_LABELS[status] ?? "Agendada";
}

export function statusTone(status: ClassSessionStatus): "scheduled" | "cancelled" | "completed" | "blocked" {
  if (status === 2) return "cancelled";
  if (status === 3) return "completed";
  if (status === 4) return "blocked";
  return "scheduled";
}

export function availableSpots(capacity: number, enrolledCount: number): number {
  return Math.max(0, capacity - enrolledCount);
}

export function isFull(entry: Pick<ScheduleEntry, "capacity" | "enrolledCount">): boolean {
  return entry.enrolledCount >= entry.capacity;
}

/**
 * Cruza a grade semanal (templates) com as sessões materializadas da semana.
 * A sessão é a fonte da verdade para status, motivo, horário e quadra quando
 * existe; sem sessão, o template vale como aula agendada.
 */
export function buildScheduleEntries(
  schedule: ClassScheduleDto,
  sessions: ClassSessionsResponse,
  options: BuildScheduleOptions,
): ScheduleEntry[] {
  const sessionsByClassAndDate = new Map(
    sessions.sessions.map(session => [`${session.classId}|${session.date.slice(0, 10)}`, session]),
  );
  const sportsById = new Map(options.sports.map(sport => [sport.id, sport.name]));

  return schedule.classes
    .map(item => {
      const session = sessionsByClassAndDate.get(`${item.id}|${item.date.slice(0, 10)}`);
      const status = session?.status ?? item.status;
      const sportId = session?.sportId ?? item.sportId;
      return {
        classId: item.id,
        sessionId: session?.id ?? null,
        date: item.date.slice(0, 10),
        name: item.name,
        sportId,
        sportName: sportId ? sportsById.get(sportId) ?? session?.sportName ?? null : null,
        coachName: session?.coachName || item.coachName,
        startTime: formatTimeInZone(session?.startTime ?? item.startTime, options.timeZoneId),
        endTime: formatTimeInZone(session?.endTime ?? item.endTime, options.timeZoneId),
        capacity: item.capacity,
        enrolledCount: item.enrolledCount,
        availableSpots: availableSpots(item.capacity, item.enrolledCount),
        status,
        reason: session?.reason ?? null,
        courtId: session?.courtId ?? schedule.courtId,
        courtName: session?.courtName ?? options.courtName,
      } satisfies ScheduleEntry;
    })
    .sort((a, b) => `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`));
}

export function groupEntriesByDate(entries: readonly ScheduleEntry[]): Map<string, ScheduleEntry[]> {
  const groups = new Map<string, ScheduleEntry[]>();
  for (const entry of entries) {
    const group = groups.get(entry.date);
    if (group) group.push(entry);
    else groups.set(entry.date, [entry]);
  }
  return groups;
}
